import React from 'react';

import Text from '~/components/core/TextComponents/Text';
import TooltipIcon from '~/components/core/TooltipIcon';
import { PolicySelectionButton } from '~/components/Fnol/FnolAiInbox/PolicySelectionButton/PolicySelectionButton';

interface RecommendedPolicyDisplayProps {
  policyNumber: string;
  onPolicySelection?: () => void;
  label?: string;
  tooltipText?: string;
  viewOnly?: boolean;
}

/**
 * RecommendedPolicyDisplay
 *
 * A reusable component that displays policy information with an interactive selection button.
 * This component shows the recommended policy with a tooltip explaining that changing the policy
 * will reprocess the FNOL with new policy details.
 *
 * @param props - Component props
 * @param props.policyNumber - The policy number
 * @param props.onPolicySelection - Callback when user wants to change/select policy
 * @param props.label - Custom label text (defaults to "Recommended Policy")
 * @param props.tooltipText - Custom tooltip text for the change policy action
 * @param props.viewOnly - Whether the policy display is in view-only mode (disables editing)
 *
 * @example
 * ```tsx
 * <RecommendedPolicyDisplay
 *   policy={selectedInboxItem.policy}
 *   onPolicySelection={() => handlePolicySelection(selectedInboxItem)}
 * />
 * ```
 */
export const RecommendedPolicyDisplay: React.FC<RecommendedPolicyDisplayProps> = ({
  policyNumber,
  onPolicySelection,
  label = 'Recommended Policy',
  tooltipText,
  viewOnly = false,
}) => {
  return (
    <div className="flex items-center gap-12">
      <Text variant={Text.VARIANTS.XS}>{label}</Text>
      <div className="w-1/2">
        {tooltipText ? (
          <TooltipIcon title={tooltipText}>
            <PolicySelectionButton policyNumber={policyNumber} onClick={onPolicySelection} viewOnly={viewOnly} />
          </TooltipIcon>
        ) : (
          <PolicySelectionButton policyNumber={policyNumber} onClick={onPolicySelection} viewOnly={viewOnly} />
        )}
      </div>
    </div>
  );
};

export default RecommendedPolicyDisplay;
